'use client'
import React, {memo, useEffect, useRef, useState} from 'react'
import {v4 as uuidv4} from 'uuid'

import ButtonList from '@/components/UiElements/ListButton'
import Button from '@/components/UiElements/Button'
import Tabs from '@/components/Tabs/Tabs'

import positionForm from './sectionTabs/PositionForm'
import laserForm from './sectionTabs/LaserForm'
import DynamicForm from '@/components/DynamicForm'

import initialize3dObject from './viewer3D/initialize3dObject'
import useSectionControl from './viewer3D/viewerHooks/internal/sections/useSectionControl'
import updateSections from './viewer3D/viewerHooks/internal/sections/updateSections'
import addSection from './viewer3D/viewerHooks/internal/sections/addSection'
import deleteSection from './viewer3D/viewerHooks/internal/sections/deleteSection'
import getSizeOfProduct from './viewer3D/viewerHooks/internal/product/getSizeOfProduct'
import {useSnapshot} from "valtio";
import store from "@/store";


const DefineSections = memo(({item, bound, data, submit, bind, change}) => {
    const containerRef = useRef(null)
    const viewerRef = useRef(null)
    const PropertyRef = useRef(null)
    const LaserRef = useRef(null)
    const [selectedTab, setSelectedTab] = useState(0)
    const [sectionsInnerValidation, setSectionsInnerValidation] = useState({})
    
    const [sectionsNew, setSectionsNew] = useState(data.sections || [])
    const [selectedSectionId, setSelectedSectionId] = useState(null)
    const [product, setProduct] = useState(data.product);
    const [statusAll, setStatusAll] = useState({
        initialized: false,
        error: null,
        loading: true
    });
    const {companySetting} = useSnapshot(store)
    
    
    // Initialize 3d object only once
    useEffect(() => {
        if (data.product && !statusAll.initialized && data?.objectFile) {
            initialize3dObject({
                product,
                status: statusAll,
                setStatus: setStatusAll,
                setProduct,
                formData: data
            })
        }
    }, [])
    
    // Handle section control
    useSectionControl({
        status: statusAll,
        selectedSectionId: selectedSectionId,
        setSelectedSectionId: setSelectedSectionId,
        product,
        sections: sectionsNew,
        setStatus: setStatusAll
    });
    
    // Tabs for section
    const tabs = {
        tabs: [
            {name: 'Property', ref: PropertyRef},
            {name: 'Laser', ref: LaserRef},
        ],
        selectedTab: selectedTab,
        onSelectedChange: async (tabIndex) => {
            setSelectedTab(tabIndex)
            updateSectionHeight(tabIndex)
        },
    }
    if (!companySetting?.machine) {
        tabs.tabs = tabs.tabs.filter((tab) => tab.name !== 'Laser')
    }
    
    // Handle tabs div height on tab change
    const updateSectionHeight = (val) => {
        if (!containerRef.current) return;
        
        const containerWidth = containerRef.current.clientWidth
        containerRef.current.children[0].style.left = 0 - (containerWidth * val + 30 * val) + 'px'
        containerRef.current.children[0].style.height = containerRef.current.children[0]?.children[val]?.clientHeight + 'px'
        containerRef.current.style.height = containerRef.current.children[0]?.children[val]?.clientHeight + 'px'
    }
    
    // Handle section validation
    const handleSectionValidation = async (sectionId, data) => {
        const validationData = data?.find((item) => item.sectionId === sectionId) || {}
        
        if (sectionId === null || sectionId === undefined) return;
        
        const innerValidation = {...sectionsInnerValidation}
        let validationErrorList = []
        const checkTabs = async () => {
            const validations = await Promise.all(
                tabs.tabs.map((tab) => tab.ref.current?.validData(validationData) || {
                    isValid: true,
                    validationErrors: []
                })
            )
            validations.forEach(({isValid, validationErrors}) => {
                if (!isValid) {
                    validationErrorList = [...Object.values(validationErrors), ...validationErrorList]
                }
            })
        }
        
        await checkTabs()
        innerValidation[sectionId] = validationErrorList //!validationResults.some(item => item.isValid === false)
        setSectionsInnerValidation(innerValidation)
        handleAllValidationResults(innerValidation)
    }
    
    // Handle change event when forms change
    const handleChange = async (sectionId) => {
        const sectionIndex = sectionsNew.findIndex((item) => item.sectionId === sectionId)
        if (sectionIndex < 0) return;
        
        const PropertyForm = (await PropertyRef?.current?.getData(true)) || {}
        const LaserForm = (await LaserRef?.current?.getData(true)) || {}
        
        const form = {
            ...PropertyForm,
            ...LaserForm,
        }
        let innerSectionsData = data.sections || []
        
        innerSectionsData[sectionIndex] = {
            ...form,
            thumbnail_image_url: sectionsNew?.[sectionIndex]?.thumbnail_image_url,
        }
        handleSectionValidation(sectionId, innerSectionsData)
        updateSectionHeight(selectedTab)
        submit(innerSectionsData)
    }
    
    // Handle first time section load
    useEffect(() => {
        tabs.onSelectedChange(0)
    }, [])
    
    // Handle form data load
    useEffect(() => {
        updateSectionHeight(selectedTab)
        let sectionListData = data.sections
        
        let withoutUuid = sectionListData.filter((item) => item?.sectionId?.toString().length < 5)
        if (withoutUuid.length > 0) {
            sectionListData = sectionListData.map((item) => {
                if (item?.sectionId?.toString().length < 5) {
                    item.sectionId = uuidv4()
                }
                return item
            })
            submit(sectionListData)
        }
        handleSetFormData()
        setSectionsNew(data.sections)
    }, [data])
    
    // Handle section data load
    useEffect(() => {
        const selectedSection = sectionsNew?.find((item) => item.sectionId === selectedSectionId)
        if (
            selectedSection !== null &&
            selectedSection !== undefined
        ) {
            
            PropertyRef.current?.setData(
                {
                    ...selectedSection,
                    ...selectedSection.userData,
                    sectionId: selectedSection.sectionId,
                    deleteSection: (item, form) => {
                        const innerSectionValidation = {...sectionsInnerValidation}
                        delete innerSectionValidation[form.sectionId]
                        setSectionsInnerValidation({...innerSectionValidation})
                        handleAllValidationResults(innerSectionValidation)
                        deleteSection(form.sectionId)
                        let sections = sectionsNew.filter((item) => item.sectionId !== form.sectionId)
                        setSelectedSectionId(sections?.[0]?.sectionId)
                        const newSectionList = sectionsNew?.filter((item) => item.sectionId !== form.sectionId)
                        submit(newSectionList)
                    },
                },
                true
            )
            
            handleSetFormData()
        }
        
        const thumbnail_image_url = sectionsNew.map((item) => item?.thumbnail_image_url)
        if (thumbnail_image_url?.length > 0) {
            change(thumbnail_image_url, 'thumbnail_image_url')
        }
    }, [selectedSectionId])
    
    const handleAllValidationResults = (validations) => {
        const allErrors = Object.values(validations).reduce((acc, curr) => acc.concat(curr), [])
        change(allErrors, 'sectionsValidations')
    }
    
    // Handle set form data when data load or section change
    const handleSetFormData = () => {
        const selectedSection = sectionsNew?.find((item) => item.sectionId === selectedSectionId)
        let sectionListData = data.sections === '' ? [] : data.sections
        
        const currentSection = sectionListData?.find((item) => item?.sectionId === selectedSectionId)
        
        if (selectedSection !== null && selectedSection !== undefined) {
            LaserRef.current?.setData({...currentSection, sectionId: selectedSection.sectionId}, true) //,
            
        }
        handleSectionValidation(selectedSectionId, sectionListData)
    }
    // Handle add section
    const handleAddSection = async () => {
        
        const size = getSizeOfProduct(product)
        const sectionData = {
            name: 'Section ' + (sectionsNew?.length + 1),
            width: 40,
            height: 40,
            offsetX: 0,
            offsetY: size.height / 2,
            radialAngle: 0,
            sectionAngle: 0,
            sectionId: uuidv4(),
        }
        await addSection({
            sectionData: sectionData,
            product,
            setSelectedSectionId
        })
        
        submit([...sectionsNew, sectionData])
    }
    
    // Handle select section
    const handleSelectSection = (sectionId) => {
        setSelectedSectionId(sectionId)
    }
    
    const handleUpdateSections3DModel = async (val) => {
        await updateSections({
            inSectionData: val,
            product,
        })
        const newSectionData = sectionsNew.map((item) => {
            let sectionData = item
            if (item.sectionId === val.sectionId) {
                sectionData = {
                    ...item,
                    ...val,
                }
            }
            return sectionData
        })
        setSectionsNew(newSectionData)
    }
    
    useEffect(() => {
        handleChange(selectedSectionId)
    }, [sectionsNew])
    
    const selectedSection = sectionsNew?.find((item) => item.sectionId === selectedSectionId)
    
    return (
        <div className={`rounded-xl bg-gray-100 p-md flex-row flex h-[calc(100vh-100px)] relative overflow-hidden`}>
            <div
                className={`absolute top-0 left-0 w-[100%] h-[100%] ${
                    ' opacity-0 z-[0] '
                } bg-gray-100  justify-center items-center  `}
            ></div>
            <div className={'flex !item-center flex-col gap-lg max-w-[100px] w-max pb-[100px] z-[1]'}>
                {sectionsNew.length > 0 && (
                    <div className={'flex flex-col gap-sm '}>
                        <ButtonList
                            className={(selected) =>
                                `w-[100px] h-[100px] bg-white rounded-md border border-gray-200 justify-center items-center p-xs hover:border-primary-500 active:shadow-focus ${
                                    selected ? 'shadow-xl border-primary-500 border-[2px]' : ''
                                }`
                            }
                            items={sectionsNew?.map((item, index) => ({
                                ...item,
                                value: item.sectionId,
                                label: (
                                    <div className={'flex justify-center items-center h-[100%] w-[100%] relative'}>
                                        {sectionsInnerValidation[item.sectionId]?.length > 0 && (
                                            <div
                                                className={
                                                    'bg-red-500 text-white text-[11px] absolute bottom-0 left-1/2 -translate-x-1/2 rounded-xs px-sm w-[100%]'
                                                }
                                            >
                                                Required
                                                {sectionsInnerValidation[item.sectionId]?.length > 0
                                                    ? `: ${sectionsInnerValidation[item.sectionId]?.length}`
                                                    : ''}
                                            </div>
                                        )}
                                        <img src={item?.thumbnail_image_url} className={'object-contain h-[100%]'}/>
                                    </div>
                                ),
                                index: index,
                            }))}
                            styles={sectionsNew.map((i, index) =>
                                sectionsInnerValidation[i.sectionId]?.length > 0
                                    ? 'border-red-500 hover:border-red-500 active:border-red-500'
                                    : ''
                            )}
                            selected={selectedSectionId}
                            onClick={(val) => {
                                handleSelectSection(val)
                            }}
                        />
                    </div>
                )}
                <div className={'flex justify-center'}>
                    <Button onClick={handleAddSection} className={'!rounded-full font-icon-solid w-[44px] h-[44px]'}>
                        +
                    </Button>
                </div>
            </div>
            <div
                ref={viewerRef} id="mainCanvas"
                className={'flex flex-grow z-[1] !w-[calc(100vw-650px)] 1transition-all overflow-hidden'}
            >
            
            </div>
            <style>{`canvas {width: 100% !important}`}</style>
            <div
                className={`z-[1] overflow-hidden relative w-[350px]`}
            >
                <div
                    className={`bg-white rounded-md  ${selectedSectionId && sectionsNew?.length > 0 ? ' !w-[350px]' : '!w-[0px]'}  flex flex-col gap-md  overflow-y-auto relative`}>
                    <div className={'p-md'}>
                        <div className={' flex flex-row justify-center item-center h-[27px] '}>
                            <div
                                className={'flex flex-grow item-center text-title-lg capitalize'}>{selectedSection?.name}</div>
                            
                            <Button
                                onClick={() => {
                                    handleSelectSection(null)
                                }}
                                className={
                                    'font-icon-solid !w-[37px] !h-[37px] overflow-hidden !items-center !justify-center flex text-[18px] !p-0 relative top-[-5px] right-[-10px]'
                                }
                                type={'link'}
                            >
                                
                            </Button>
                        </div>
                        <div className={'border-b border-b-gray-200'}>
                            <Tabs tabs={tabs.tabs} selectedTab={selectedTab} onSelectedChange={tabs.onSelectedChange}
                                  size={'sm'}/>
                        </div>
                    </div>
                    {' '}
                    <div
                        className={`overflow-hidden transition-all max-h-[calc(100vh-245px)] relative duration-500 `}
                        ref={containerRef}
                        style={{overflowY: 'auto', overflowX: 'hidden'}}
                    >
                        <div
                            className="flex items-start gap-[30px] absolute top-0 flex-row transition-all duration-500 "
                            style={{height: '100%'}}
                        >
                            <>
                                <div className={'w-[350px] px-md'}>
                                    <div className={'grid grid-cols-1 gap-md !w-[100%] pb-[20px] '}>
                                        <DynamicForm
                                            items={positionForm}
                                            size={'md'}
                                            ref={PropertyRef}
                                            onChange={(val) => {
                                                handleUpdateSections3DModel(val)
                                            }}
                                            validOnChange={true}
                                        />
                                    </div>
                                    {' '}
                                </div>
                                <div className={'w-[350px] px-md'}>
                                    <div className={'grid grid-cols-12 gap-md  w-[100%] pb-[20px]'}>
                                        <DynamicForm
                                            items={laserForm}
                                            size={'sm'}
                                            ref={LaserRef}
                                            onChange={(val) => {
                                                handleChange(val.sectionId)
                                            }}
                                            validOnChange={true}
                                        />
                                    </div>
                                </div>
                                {' '}
                                <div className={'w-[350px] px-md'}>
                                    {' '}
                                </div>
                            </>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})
DefineSections.displayName = 'DefineSections'
export default DefineSections
